/**
 * RoRitmo — Scroll Animation Controller
 * Lightweight Intersection Observer for scroll-triggered animations
 * Works with data-animate attributes defined in home-animations.css
 */
(function () {
  'use strict';

  /* ─── Configuration ─── */
  var DEFAULTS = {
    threshold: 0.15,       // 15% of element must be visible
    rootMargin: '0px 0px -40px 0px', // trigger slightly before full view
    visibleClass: 'rr-visible',
    animateAttr: 'data-animate',
    delayAttr: 'data-animate-delay',
    staggerAttr: 'data-animate-stagger',
    onceAttr: 'data-animate-once'       // if present, animate only once
  };

  /* ─── Respect reduced motion ─── */
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function shouldAnimate() {
    return !prefersReducedMotion.matches;
  }

  /* ─── Apply stagger delays to children ─── */
  function applyStagger(parent) {
    var staggerMs = parseInt(parent.getAttribute(DEFAULTS.staggerAttr), 10);
    if (isNaN(staggerMs) || staggerMs <= 0) staggerMs = 100;

    var children = parent.children;
    for (var i = 0; i < children.length; i++) {
      children[i].style.setProperty('--rr-child-index', i);
      // Also set transition-delay directly for elements with data-animate
      if (children[i].hasAttribute(DEFAULTS.animateAttr)) {
        children[i].style.transitionDelay = (i * staggerMs) + 'ms';
      }
    }
  }

  /* ─── Animate Counters ─── */
  function animateCounters(parent) {
    var counters = parent.querySelectorAll('.rr-counter');
    for (var i = 0; i < counters.length; i++) {
      var counter = counters[i];
      if (counter.classList.contains('rr-counted')) continue;

      var text = counter.innerText.trim();
      var numMatch = text.match(/^([^\d]*)([\d.]+)([^\d]*)$/);
      if (!numMatch) continue;

      counter.classList.add('rr-counted');
      
      (function(el, prefix, target, suffix) {
        var duration = 1500;
        var startTime = null;
        var hasPad = el.innerText.trim().startsWith('0') && target < 10;
        
        function step(timestamp) {
          if (!startTime) startTime = timestamp;
          var progress = Math.min((timestamp - startTime) / duration, 1);
          var current = Math.floor(progress * target);
          var currentStr = current.toString();
          if (hasPad && current < 10) {
            currentStr = "0" + currentStr;
          }
          el.innerText = prefix + currentStr + suffix;
          if (progress < 1) {
            window.requestAnimationFrame(step);
          } else {
            var finalStr = target.toString();
            if (hasPad && target < 10) {
              finalStr = "0" + finalStr;
            }
            el.innerText = prefix + finalStr + suffix;
          }
        }
        window.requestAnimationFrame(step);
      })(counter, numMatch[1], parseFloat(numMatch[2]), numMatch[3]);
    }
  }

  /* ─── Observer callback ─── */
  function onIntersect(entries, observer) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        var el = entry.target;

        // Apply custom delay if specified
        var delay = el.getAttribute(DEFAULTS.delayAttr);
        if (delay) {
          el.style.transitionDelay = delay + 'ms';
        }

        // Add visible class
        el.classList.add(DEFAULTS.visibleClass);

        // Animate any counters inside this element
        animateCounters(el);
        if (el.classList.contains('rr-counter')) {
          animateCounters(el.parentElement || document.body);
        }

        // If animate-once, stop observing
        var once = el.getAttribute(DEFAULTS.onceAttr);
        if (once === null || once === 'true' || once === '') {
          // Default behavior: animate once
          observer.unobserve(el);
        }
      } else {
        // Only remove class if element is NOT set to animate once
        var el2 = entry.target;
        var once2 = el2.getAttribute(DEFAULTS.onceAttr);
        if (once2 === 'false') {
          el2.classList.remove(DEFAULTS.visibleClass);
        }
      }
    });
  }

  /* ─── Initialize ─── */
  function init() {
    // If reduced motion, make everything visible immediately
    if (!shouldAnimate()) {
      var allAnimated = document.querySelectorAll('[' + DEFAULTS.animateAttr + ']');
      for (var i = 0; i < allAnimated.length; i++) {
        allAnimated[i].classList.add(DEFAULTS.visibleClass);
      }
      return;
    }

    // Check for IntersectionObserver support
    if (!('IntersectionObserver' in window)) {
      // Fallback: show everything
      var fallback = document.querySelectorAll('[' + DEFAULTS.animateAttr + ']');
      for (var j = 0; j < fallback.length; j++) {
        fallback[j].classList.add(DEFAULTS.visibleClass);
      }
      return;
    }

    // Create observer
    var observer = new IntersectionObserver(onIntersect, {
      threshold: DEFAULTS.threshold,
      rootMargin: DEFAULTS.rootMargin
    });

    // Process stagger parents first
    var staggerParents = document.querySelectorAll('[' + DEFAULTS.staggerAttr + ']');
    for (var k = 0; k < staggerParents.length; k++) {
      applyStagger(staggerParents[k]);
    }

    // Observe all animated elements
    var animatedEls = document.querySelectorAll('[' + DEFAULTS.animateAttr + ']');
    for (var l = 0; l < animatedEls.length; l++) {
      var el = animatedEls[l];
      observer.observe(el);
      
      // Immediately activate elements already in/overlapping the viewport to avoid issues during AJAX replacements
      var rect = el.getBoundingClientRect();
      var inViewport = rect.top < window.innerHeight && rect.bottom > 0;
      if (inViewport) {
        el.classList.add(DEFAULTS.visibleClass);
        animateCounters(el);
      }
    }

    // Also observe elements with rr-underline-grow or rr-heading-decorated
    var decoratedEls = document.querySelectorAll('.rr-underline-grow, .rr-heading-decorated');
    for (var m = 0; m < decoratedEls.length; m++) {
      if (!decoratedEls[m].hasAttribute(DEFAULTS.animateAttr)) {
        observer.observe(decoratedEls[m]);
      }
    }
  }

  /* ─── Start on DOM ready ─── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ─── Re-initialize for Shopify section rendering ─── */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      setTimeout(init, 100);
    });
  }

  /* ─── Listen for reduced motion changes ─── */
  prefersReducedMotion.addEventListener('change', function () {
    if (!shouldAnimate()) {
      var all = document.querySelectorAll('[' + DEFAULTS.animateAttr + ']');
      for (var i = 0; i < all.length; i++) {
        all[i].classList.add(DEFAULTS.visibleClass);
      }
    }
  });
  window.initRoRitmoAnimations = init;
})();
